import { useState } from 'react';
import type { Usuario } from '../lib/api.ts';
import { BotaoSigilo } from './BotaoSigilo.tsx';
import { Busca } from './Busca.tsx';
import { Icone, type NomeIcone } from './Icone.tsx';
import { usarEhMaster } from '../lib/papel.ts';
import { MenuUsuario } from './MenuUsuario.tsx';

/**
 * As seis abas do KairósHub, na ordem do `docs/PRD.md` §4.2 — agora no MENU
 * LATERAL, que substituiu o cabeçalho de abas em 14/09/2026 (sistema visual
 * 2026, aprovado por ele vendo a proposta). Ver `estilos/casca.css`.
 *
 * As que ainda não existem aparecem apagadas, e clicar nelas explica o que
 * serão e quando. Mostrar o mapa inteiro com o caminho já andado é honesto;
 * esconder o que falta faria o Hub parecer menor do que o projeto é.
 *
 * ⚠️ **A aba futura não carrega mais o selo "em breve"** — decisão do Richard,
 * 15/08/2026, quando a navegação passou a caber numa linha só. Quatro selos ao
 * lado de seis ícones disputavam atenção com as duas abas que fazem o trabalho
 * de hoje. O apagado já diz que ela não está pronta, e o clique explica o resto.
 */
type Aba = {
  id: string;
  nome: string;
  icone: NomeIcone;
  caminho: string;
  pronta: boolean;
  promessa: string;
  /**
   * A aba nem aparece para quem não é master.
   *
   * Diferente de `pronta: false`, que diz "ainda vem". Aqui é "não é sua" — e
   * mostrar uma aba que responde 403 seria oferecer o que não se pode dar.
   */
  soMaster?: boolean;
};

const ABAS: Aba[] = [
  {
    id: 'calendario',
    nome: 'Calendário',
    icone: 'calendario',
    caminho: '/calendario',
    pronta: true,
    promessa: 'Prazos, reuniões e feriados.',
  },
  {
    id: 'clientes',
    nome: 'Clientes',
    icone: 'clientes',
    caminho: '/clientes',
    pronta: true,
    promessa: 'Ficha do cliente e seus casos.',
  },
  {
    id: 'comercial',
    nome: 'Comercial',
    icone: 'comercial',
    caminho: '/comercial',
    /* ⭐ No ar desde 20/09/2026 (PRD §49), com as sete etapas definidas e as 120
       empresas do levantamento comercial dentro. */
    pronta: true,
    promessa: 'O funil de vendas, do primeiro contato à proposta assinada.',
  },
  {
    id: 'financeiro',
    caminho: '/financeiro',
    nome: 'Financeiro',
    icone: 'financeiro',
    pronta: true,
    soMaster: true,
    promessa:
      'Honorário contratado, o que entrou e o que está para vencer. Incremento 5.',
  },
  {
    id: 'relatorios',
    caminho: '/relatorios',
    nome: 'Relatórios',
    icone: 'relatorios',
    pronta: true,
    promessa:
      'Geração do relatório do nível no padrão visual da KAIRÓS. Incremento 7.',
  },
  {
    id: 'marketing',
    caminho: '/marketing',
    nome: 'Marketing',
    icone: 'marketing',
    /* ⭐ A parte 1, o calendário editorial (15/10/2026, PRD §50.4). ⛔ Só
       do master, decisão dele, como o Financeiro: para o colaborador a aba
       some, e o servidor recusa toda rota. Nasce sem conexão com o banco de
       casos: fronteira de arquitetura, não configuração (PRD §7.1). */
    pronta: true,
    soMaster: true,
    promessa: 'O calendário editorial do Instagram e do LinkedIn.',
  },
  /* ⭐ PRD §75 (29/10/2026): "Modelos de IA" saiu, a pedido dele ("só polui o Hub com mais e mais abas"): o comitê
     automático da Ester & Cia. decide, o Hub prova, testa, troca e volta sozinho, e a Ester conta no Telegram. */
];

type Props = {
  /** Recolhido: só o filete na borda, e o menu abre por cima ao passar o mouse. */
  recolhido: boolean;
  aoAlternarMenu: () => void;
  usuario: Usuario;
  caminho: string;
  aoNavegar: (caminho: string) => void;
  aoTrocarSenha: () => void;
  aoCriarUsuario: () => void;
  aoSair: () => void;
};

/**
 * Qual item está aceso.
 *
 * ⛔ **Relatórios nunca acendia** — a função antiga conhecia Clientes,
 * Calendário e Financeiro, e quem estava num relatório via o cabeçalho sem
 * nenhuma aba marcada. Consertado junto com a troca de lugar, 14/09/2026.
 */
function abaDoCaminho(caminho: string): string {
  if (caminho === '/') return 'hoje';
  if (caminho.startsWith('/clientes')) return 'clientes';
  if (caminho.startsWith('/calendario')) return 'calendario';
  if (caminho.startsWith('/financeiro')) return 'financeiro';
  if (caminho.startsWith('/relatorios')) return 'relatorios';
  /* ⚠️ Faltava desde 20/09/2026: a aba subiu e o menu não a marcava, nem com a
     lista aberta. Com a ficha da empresa em `/comercial/:id`, a falta ficaria
     pior — a pessoa navegaria para dentro sem nada aceso no menu. */
  if (caminho.startsWith('/comercial')) return 'comercial';
  /* ⭐ 15/10/2026: a aba subiu já sabendo onde acende. */
  if (caminho.startsWith('/marketing')) return 'marketing';
  return '';
}

/**
 * Os dois grupos do menu: o trabalho do dia e a gestão.
 *
 * ⚠️ **A ordem mudou com o lugar, e o conjunto não.** Numa coluna, lê-se de
 * cima para baixo o caminho de um caso — o dia, a agenda, o cliente, o
 * documento —, e o que é gestão fica separado. Nenhuma aba entrou ou saiu;
 * "Hoje" sempre existiu, atrás do logotipo, e passou a ter nome.
 */
const TRABALHO = ['calendario', 'clientes', 'relatorios'];
const GESTAO = ['financeiro', 'comercial', 'marketing'];

export function Lateral({
  recolhido,
  aoAlternarMenu,
  usuario,
  caminho,
  aoNavegar,
  aoTrocarSenha,
  aoCriarUsuario,
  aoSair,
}: Props) {
  const [espiando, setEspiando] = useState<Aba | null>(null);
  const abaAtiva = abaDoCaminho(caminho);
  const ehMaster = usarEhMaster();

  const item = (aba: Aba) => (
    <button
      key={aba.id}
      type="button"
      className={
        'lateral-item' +
        (aba.id === abaAtiva ? ' lateral-item-ativo' : '') +
        (aba.pronta ? '' : ' lateral-item-futuro')
      }
      aria-current={aba.id === abaAtiva ? 'page' : undefined}
      /* A aba futura não carrega selo, então quem usa leitor de tela precisa
         ouvir daqui que ela ainda não abre. */
      aria-label={aba.pronta ? undefined : `${aba.nome} — em breve`}
      onClick={() => {
        if (aba.pronta) {
          setEspiando(null);
          return aoNavegar(aba.caminho);
        }
        setEspiando(espiando?.id === aba.id ? null : aba);
      }}
    >
      <Icone nome={aba.icone} />
      <span>{aba.nome}</span>
    </button>
  );

  const grupo = (ids: string[]) =>
    ids
      .map((id) => ABAS.find((a) => a.id === id)!)
      .filter((aba) => !aba.soMaster || ehMaster);

  const promessa = (ids: string[]) =>
    espiando && ids.includes(espiando.id) ? (
      <p className="lateral-promessa">
        <strong>{espiando.nome}</strong> — {espiando.promessa}
      </p>
    ) : null;

  return (
    <aside className={'lateral' + (recolhido ? ' lateral-recolhida' : '')} aria-label="Menu do Hub">
      {/* ⭐ Recolher e fixar — 14/09/2026, pedido dele. Aberto por cima (com o
          menu recolhido), o mesmo botão fixa o menu de volta. */}
      <button
        type="button"
        className="lateral-alternar"
        onClick={aoAlternarMenu}
        aria-label={recolhido ? 'Fixar o menu aberto' : 'Recolher o menu'}
        title={recolhido ? 'Fixar o menu aberto' : 'Recolher o menu'}
      >
        <Icone nome={recolhido ? 'menu-fixar' : 'menu-recolher'} tamanho={16} />
      </button>

      {/* O logo leva para a tela Hoje — é o "início" do Hub.
          O wordmark é o arquivo original da marca, nunca texto recriado:
          `docs/MARCA.md` §5 diz que a fonte KELVinch é exclusiva do logotipo
          e §3 proíbe recriá-lo em outra fonte. */}
      <button
        type="button"
        className="marca"
        onClick={() => aoNavegar('/')}
        title="Ir para Hoje"
        aria-label="KAIRÓS Hub — ir para Hoje"
      >
        <img className="marca-completa" src="/marca/kairos-branco.png" alt="KAIRÓS Asset Intelligence" />
        <span className="marca-hub">Hub</span>
      </button>

      <Busca aoNavegar={aoNavegar} variante="campo" />

      <nav className="lateral-grupo" aria-label="Trabalho">
        <p className="lateral-titulo">Trabalho</p>
        <button
          type="button"
          className={'lateral-item' + (abaAtiva === 'hoje' ? ' lateral-item-ativo' : '')}
          aria-current={abaAtiva === 'hoje' ? 'page' : undefined}
          onClick={() => {
            setEspiando(null);
            aoNavegar('/');
          }}
        >
          <Icone nome="hoje" />
          <span>Hoje</span>
        </button>
        {grupo(TRABALHO).map(item)}
        {promessa(TRABALHO)}
      </nav>

      <nav className="lateral-grupo" aria-label="Gestão">
        <p className="lateral-titulo">Gestão</p>
        {grupo(GESTAO).map(item)}
        {promessa(GESTAO)}
      </nav>

      <div className="lateral-pe">
        <BotaoSigilo variante="linha" />
        <MenuUsuario
          usuario={usuario}
          aoTrocarSenha={aoTrocarSenha}
          aoCriarUsuario={aoCriarUsuario}
          aoSair={aoSair}
          variante="lateral"
        />
      </div>
    </aside>
  );
}
